import React, { useState, useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card'
import { Button } from '../components/ui/button'
import { useAuthStore } from '../stores/authStore'
import { API_CONFIG } from '../lib/config'
import {
    TrendingUp,
    Clock,
    BarChart3,
    Activity,
    Target,
    RefreshCw,
    Play,
    Pause,
    Calendar,
    TrendingDown,
    AlertTriangle,
    Package,
    ArrowDown,
    ArrowUp,
    Search
} from 'lucide-react'
import { Line } from 'react-chartjs-2'
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
} from 'chart.js'

// Chart.js bileşenlerini kaydet
ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend
)

interface VelocityChartData {
    calculatedAt: string
    velocityScore: number
    stockDecreaseRate: number
    salesVelocity: number
    trend: 'INCREASING' | 'DECREASING' | 'STABLE' | 'VOLATILE'
}

interface Product {
    id: string
    sku: string
    name: string
    stock: number
    price: number
    category?: string
    brand?: string
}

interface StockDifference {
    id: string
    sku: string
    previousStock: number
    currentStock: number
    difference: number
    timestamp: string
    product: {
        id: string
        name: string
        image1?: string
        image2?: string
        image3?: string
        image4?: string
        image5?: string
    }
}

interface StockDifferencesResponse {
    differences: StockDifference[]
    total: number
}

interface DepletedProduct {
    id: string
    sku: string
    name: string
    stock: number
    price: number
    lastUpdated: string
    dateAdd?: string
    createdAt: string
    image1?: string
    image2?: string
    image3?: string
    image4?: string
    image5?: string
    depletedAt?: string
}

export default function StockTrackingPage() {
    const [selectedProduct, setSelectedProduct] = useState<string>('')
    const [isLoading, setIsLoading] = useState(false)
    const [selectedImage, setSelectedImage] = useState<string>('')
    const [showImageModal, setShowImageModal] = useState(false)
    const [productSearch, setProductSearch] = useState<string>('')
    const [filteredProducts, setFilteredProducts] = useState<Product[]>([])

    const { accessToken } = useAuthStore()

    // Stok farklarını getir
    const { data: stockDifferences, isLoading: isLoadingDifferences, error: differencesError, refetch: refetchDifferences } = useQuery<StockDifferencesResponse>({
        queryKey: ['stockDifferences'],
        queryFn: async () => {
            const response = await fetch(`${API_CONFIG.BASE_URL}/api/stock-tracking/differences?limit=50`, {
                headers: {
                    'Authorization': `Bearer ${accessToken}`
                }
            })
            if (!response.ok) throw new Error('Stok farkları yüklenemedi')
            return response.json()
        },
        enabled: !!accessToken,
        staleTime: Infinity, // Cache'te sonsuz süre tut
        gcTime: Infinity // Garbage collection'dan koru
    })

    // Tüm ürünleri getir (cache'ten veya API'den)
    const { data: allProducts, refetch: refetchAllProducts, error: productsError, isLoading: isLoadingProducts } = useQuery<Product[]>({
        queryKey: ['allProducts'],
        queryFn: async () => {
            console.log('Tüm ürünler yükleniyor (tek istek)...', { accessToken: !!accessToken })

            const response = await fetch(`${API_CONFIG.BASE_URL}/api/products/all`, {
                headers: {
                    'Authorization': `Bearer ${accessToken}`
                }
            })

            if (!response.ok) {
                const errorText = await response.text()
                console.error('API Error:', errorText)
                throw new Error(`Ürünler yüklenemedi: ${response.status} ${response.statusText}`)
            }

            const data = await response.json()
            console.log(`Toplam ${data.products.length} ürün yüklendi (tek istek)`)

            return data.products
        },
        enabled: !!accessToken, // AccessToken varsa otomatik yükle
        staleTime: Infinity, // Cache'de süresiz tut
        gcTime: Infinity // Cache'de süresiz tut (yeni react-query sürümünde cacheTime -> gcTime)
    })

    // Ürün arama filtreleme
    useEffect(() => {
        if (allProducts && allProducts.length > 0) {
            const filtered = allProducts.filter(product =>
                product.name.toLowerCase().includes(productSearch.toLowerCase()) ||
                product.sku.toLowerCase().includes(productSearch.toLowerCase()) ||
                (product.category && product.category.toLowerCase().includes(productSearch.toLowerCase())) ||
                (product.brand && product.brand.toLowerCase().includes(productSearch.toLowerCase()))
            )
            setFilteredProducts(filtered)
        }
    }, [allProducts, productSearch])

    // Ürün stok geçmişi grafiği (sınırsız)
    const { data: chartData, refetch: refetchChart } = useQuery<any[]>({
        queryKey: ['productStockHistory', selectedProduct],
        queryFn: async () => {
            if (!selectedProduct) {
                console.log('selectedProduct yok:', selectedProduct)
                return []
            }

            console.log('Stok geçmişi yükleniyor, productId:', selectedProduct)
            const response = await fetch(`${API_CONFIG.BASE_URL}/api/stock-tracking/product-stock-history/${selectedProduct}`, {
                headers: {
                    'Authorization': `Bearer ${accessToken}`
                }
            })

            console.log('Stok geçmişi API response:', response.status, response.statusText)

            if (!response.ok) {
                const errorText = await response.text()
                console.error('Stok geçmişi API hatası:', errorText)
                throw new Error('Ürün stok geçmişi yüklenemedi')
            }

            const data = await response.json()
            console.log('Stok geçmişi verisi:', data.history?.length || 0, 'kayıt')

            // Sadece stok değişimlerinin olduğu snapshot'ları filtrele
            const history = data.history || []
            const filteredHistory = history.filter((snapshot: any, index: number) => {
                // İlk snapshot her zaman dahil edilir
                if (index === 0) return true

                // Önceki snapshot ile stok değeri farklıysa dahil et
                const previousSnapshot = history[index - 1]
                return snapshot.stock !== previousSnapshot.stock
            })

            console.log('Filtrelenmiş stok geçmişi:', filteredHistory.length, 'kayıt (değişim olan)')
            return filteredHistory
        },
        enabled: !!accessToken && !!selectedProduct
    })

    // Son 1 haftada stoğu sıfıra düşen ürünler
    const { data: weeklyDepletedProducts, isLoading: isLoadingDepleted, error: depletedError, refetch: refetchDepleted } = useQuery<DepletedProduct[]>({
        queryKey: ['weeklyDepletedProducts'],
        queryFn: async () => {
            const response = await fetch(`${API_CONFIG.BASE_URL}/api/stock-tracking/weekly-depleted-products`, {
                headers: {
                    'Authorization': `Bearer ${accessToken}`
                }
            })
            if (!response.ok) throw new Error('Son 1 haftada stoğu sıfıra düşen ürünler yüklenemedi')
            const data = await response.json()
            return data.depletedProducts || []
        },
        enabled: !!accessToken,
        refetchInterval: 60000 // Her 1 dakika yenile
    })

    const handleManualTracking = async () => {
        setIsLoading(true)
        try {
            const response = await fetch(`${API_CONFIG.BASE_URL}/api/stock-tracking/manual-tracking`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${accessToken}`,
                    'Content-Type': 'application/json'
                }
            })
            if (!response.ok) throw new Error('Manuel stok takibi başlatılamadı')
            alert('Manuel stok takibi başlatıldı!')
            refetchAllProducts()
        } catch (error) {
            alert('Hata: ' + (error instanceof Error ? error.message : 'Bilinmeyen hata'))
        } finally {
            setIsLoading(false)
        }
    }

    const handleMidnightTracking = async () => {
        setIsLoading(true)
        try {
            const response = await fetch(`${API_CONFIG.BASE_URL}/api/stock-tracking/midnight-tracking`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${accessToken}`,
                    'Content-Type': 'application/json'
                }
            })
            if (!response.ok) throw new Error('Gece yarısı stok takibi başlatılamadı')
            alert('Gece yarısı stok takibi başlatıldı!')
            refetchAllProducts()
        } catch (error) {
            alert('Hata: ' + (error instanceof Error ? error.message : 'Bilinmeyen hata'))
        } finally {
            setIsLoading(false)
        }
    }

    const handleCheckStockAlerts = async () => {
        setIsLoading(true)
        try {
            const response = await fetch(`${API_CONFIG.BASE_URL}/api/stock-tracking/check-stock-alerts`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${accessToken}`,
                    'Content-Type': 'application/json'
                }
            })
            if (!response.ok) throw new Error('Stok uyarıları kontrol edilemedi')
            alert('Son 1 günlük stok uyarıları kontrol edildi ve Telegram\'a gönderildi!')
        } catch (error) {
            alert('Hata: ' + (error instanceof Error ? error.message : 'Bilinmeyen hata'))
        } finally {
            setIsLoading(false)
        }
    }

    const handleSendTelegramAlertWeekly = async () => {
        setIsLoading(true)
        try {
            console.log('Telegram uyarısı gönderiliyor...')
            const response = await fetch(`${API_CONFIG.BASE_URL}/api/stock-tracking/check-stock-alerts-weekly`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${accessToken}`,
                    'Content-Type': 'application/json'
                }
            })

            console.log('Response status:', response.status)
            const responseText = await response.text()
            console.log('Response:', responseText)

            if (!response.ok) {
                throw new Error(`HTTP ${response.status}: ${responseText}`)
            }

            alert('Son 7 günlük stok uyarıları kontrol edildi ve Telegram\'a gönderildi!')
        } catch (error) {
            console.error('Telegram uyarı hatası:', error)
            alert('Hata: ' + (error instanceof Error ? error.message : 'Bilinmeyen hata'))
        } finally {
            setIsLoading(false)
        }
    }


    const handleSendDailySummary = async () => {
        setIsLoading(true)
        try {
            const response = await fetch(`${API_CONFIG.BASE_URL}/api/stock-tracking/send-daily-summary`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${accessToken}`,
                    'Content-Type': 'application/json'
                }
            })
            if (!response.ok) throw new Error('Günlük özet raporu gönderilemedi')
            alert('Günlük stok özet raporu Telegram\'a gönderildi!')
        } catch (error) {
            alert('Hata: ' + (error instanceof Error ? error.message : 'Bilinmeyen hata'))
        } finally {
            setIsLoading(false)
        }
    }

    const handleRefreshData = async () => {
        setIsLoading(true)
        try {
            // XML'den veri çek ve veritabanına kaydet
            const response = await fetch(`${API_CONFIG.BASE_URL}/api/stock-tracking/refresh-xml-data`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${accessToken}`,
                    'Content-Type': 'application/json'
                }
            })
            if (!response.ok) throw new Error('XML verileri yenilenemedi')

            const result = await response.json()

            // Stok farklarını yenile
            await refetchDifferences()
            await refetchAllProducts()
            await refetchDepleted()

            // Senkronizasyon sonuçlarını console'a yaz
            console.log('Veriler başarıyla yenilendi!')
            if (result.syncResults) {
                const { products } = result.syncResults
                if (products) {
                    console.log(`Ürünler: ${products.total} toplam, ${products.created} yeni, ${products.updated} güncellendi`)
                }
            }
        } catch (error: any) {
            console.error('Hata: ' + (error instanceof Error ? error.message : 'Bilinmeyen hata'))
        } finally {
            setIsLoading(false)
        }
    }

    const handleSendTelegramAlert = async () => {
        setIsLoading(true)
        try {
            const response = await fetch(`${API_CONFIG.BASE_URL}/api/stock-tracking/check-stock-alerts`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${accessToken}`,
                    'Content-Type': 'application/json'
                }
            })
            if (!response.ok) throw new Error('Telegram uyarısı gönderilemedi')
            alert('Stok uyarıları Telegram\'a gönderildi!')
        } catch (error: any) {
            alert('Hata: ' + (error instanceof Error ? error.message : 'Bilinmeyen hata'))
        } finally {
            setIsLoading(false)
        }
    }

    const handleSendProductToTelegram = async (product: StockDifference) => {
        setIsLoading(true)
        try {
            console.log('Tekil ürün Telegram gönderiliyor:', product)
            const response = await fetch(`${API_CONFIG.BASE_URL}/api/stock-tracking/send-product-alert`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${accessToken}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    productId: product.product.id,
                    sku: product.sku,
                    previousStock: product.previousStock,
                    currentStock: product.currentStock,
                    difference: product.difference
                })
            })

            console.log('Response status:', response.status)
            const responseText = await response.text()
            console.log('Response:', responseText)

            if (!response.ok) {
                throw new Error(`HTTP ${response.status}: ${responseText}`)
            }

            alert(`${product.sku} ürünü Telegram\'a gönderildi!`)
        } catch (error: any) {
            console.error('Tekil ürün Telegram hatası:', error)
            alert('Hata: ' + (error instanceof Error ? error.message : 'Bilinmeyen hata'))
        } finally {
            setIsLoading(false)
        }
    }

    const handleImageClick = (imageUrl: string) => {
        setSelectedImage(imageUrl)
        setShowImageModal(true)
    }

    const getTrendIcon = (trend: string) => {
        switch (trend) {
            case 'INCREASING': return <TrendingUp className="w-4 h-4 text-green-600" />
            case 'DECREASING': return <TrendingDown className="w-4 h-4 text-red-600" />
            case 'VOLATILE': return <Activity className="w-4 h-4 text-yellow-600" />
            default: return <Target className="w-4 h-4 text-blue-600" />
        }
    }

    const getTrendColor = (trend: string) => {
        switch (trend) {
            case 'INCREASING': return 'text-green-600 bg-green-100'
            case 'DECREASING': return 'text-red-600 bg-red-100'
            case 'VOLATILE': return 'text-yellow-600 bg-yellow-100'
            default: return 'text-blue-600 bg-blue-100'
        }
    }

    return (
        <div className="p-3 sm:p-6 lg:p-8">
            {/* Başlık ve Kontroller */}
            <Card className="mb-6 sm:mb-8 shadow-xl border-0 bg-white/80 backdrop-blur-sm">
                <CardHeader className="pb-3 sm:pb-4">
                    <CardTitle className="flex items-center text-lg sm:text-xl">
                        <BarChart3 className="w-5 h-5 sm:w-6 sm:h-6 mr-2 sm:mr-3 text-blue-600" />
                        Stok Takip ve Velocity Analizi
                    </CardTitle>
                </CardHeader>
                <CardContent className="px-4 sm:px-6">
                    <div className="flex flex-col sm:flex-row justify-center gap-3 sm:gap-4">
                        <Button
                            onClick={handleRefreshData}
                            disabled={isLoading}
                            className="bg-green-600 hover:bg-green-700 text-white h-10 sm:h-12 px-4 sm:px-8 text-sm sm:text-base"
                        >
                            <RefreshCw className={`w-4 h-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
                            <span className="hidden sm:inline">Verileri Yenile</span>
                            <span className="sm:hidden">Yenile</span>
                            <span className="hidden lg:inline text-xs ml-2 opacity-75">(güncel grafikler için)</span>
                        </Button>

                        <Button
                            onClick={handleSendTelegramAlert}
                            disabled={isLoading}
                            className="bg-blue-600 hover:bg-blue-700 text-white h-10 sm:h-12 px-4 sm:px-8 text-sm sm:text-base"
                        >
                            <span className="text-lg mr-2">📱</span>
                            <span className="hidden sm:inline">Telegram (Son 1 Gün)</span>
                            <span className="sm:hidden">1 Gün</span>
                        </Button>

                        <Button
                            onClick={handleSendTelegramAlertWeekly}
                            disabled={isLoading}
                            className="bg-purple-600 hover:bg-purple-700 text-white h-10 sm:h-12 px-4 sm:px-8 text-sm sm:text-base"
                        >
                            <span className="text-lg mr-2">📱</span>
                            <span className="hidden sm:inline">Telegram (Son 7 Gün)</span>
                            <span className="sm:hidden">7 Gün</span>
                        </Button>
                    </div>

                </CardContent>
            </Card>

            {/* Stok Farkları */}
            <Card className="mb-6 sm:mb-8 shadow-xl border-0 bg-white/80 backdrop-blur-sm">
                <CardHeader className="pb-3 sm:pb-4">
                    <CardTitle className="flex items-center text-lg sm:text-xl">
                        <AlertTriangle className="w-5 h-5 sm:w-6 sm:h-6 mr-2 sm:mr-3 text-red-600" />
                        Stok Farkları
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    {isLoadingDifferences ? (
                        <div className="text-center py-8">
                            <RefreshCw className="w-6 h-6 animate-spin mx-auto mb-2" />
                            <p>Stok farkları yükleniyor...</p>
                        </div>
                    ) : differencesError ? (
                        <div className="text-center py-8 text-red-600">
                            <p>Hata: {differencesError.message}</p>
                        </div>
                    ) : stockDifferences?.differences && stockDifferences.differences.length > 0 ? (
                        <div className="space-y-3 sm:space-y-4 max-h-96 overflow-y-auto">
                            {stockDifferences.differences.map((diff) => (
                                <div key={diff.id} className="flex flex-col sm:flex-row items-start sm:items-center justify-between p-3 sm:p-4 bg-gradient-to-r from-red-50 to-orange-50 rounded-lg border border-red-200 gap-3 sm:gap-4">
                                    <div className="flex items-center space-x-3 sm:space-x-4 w-full sm:w-auto">
                                        {/* Ürün Görseli - Mobil Uyumlu */}
                                        <div
                                            className="w-16 h-16 sm:w-20 sm:h-20 lg:w-24 lg:h-24 bg-gray-100 rounded-lg flex items-center justify-center overflow-hidden cursor-pointer hover:shadow-lg transition-shadow flex-shrink-0"
                                            onClick={() => diff.product.image1 && handleImageClick(diff.product.image1)}
                                        >
                                            {diff.product.image1 ? (
                                                <img
                                                    src={diff.product.image1}
                                                    alt={diff.product.name}
                                                    className="w-full h-full object-cover hover:scale-105 transition-transform"
                                                />
                                            ) : (
                                                <Package className="w-6 h-6 sm:w-8 sm:h-8 lg:w-12 lg:h-12 text-gray-400" />
                                            )}
                                        </div>

                                        {/* Ürün Bilgileri */}
                                        <div className="flex-1 min-w-0">
                                            <p className="font-medium text-gray-800 text-sm sm:text-base truncate">{diff.product.name}</p>
                                            <p className="text-xs sm:text-sm text-gray-600">SKU: {diff.sku}</p>
                                            <div className="flex flex-col sm:flex-row sm:items-center space-y-1 sm:space-y-0 sm:space-x-4 mt-1">
                                                <span className="text-xs sm:text-sm text-gray-600">
                                                    Önceki: {diff.previousStock}
                                                </span>
                                                <span className="text-xs sm:text-sm text-gray-600">
                                                    Şimdi: {diff.currentStock}
                                                </span>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Sağ Taraf - Fark ve Telegram Butonu */}
                                    <div className="flex items-center justify-between sm:justify-end space-x-3 sm:space-x-4 w-full sm:w-auto">
                                        {/* Fark Göstergesi */}
                                        <div className="text-right">
                                            <div className={`flex items-center space-x-2 ${diff.difference < 0 ? 'text-red-600' : 'text-green-600'
                                                }`}>
                                                {diff.difference < 0 ? (
                                                    <ArrowDown className="w-4 h-4 sm:w-5 sm:h-5" />
                                                ) : (
                                                    <ArrowUp className="w-4 h-4 sm:w-5 sm:h-5" />
                                                )}
                                                <span className="text-sm sm:text-lg font-bold">
                                                    {diff.difference > 0 ? '+' : ''}{diff.difference}
                                                </span>
                                            </div>
                                            <p className="text-xs text-gray-500 mt-1">
                                                {new Date(diff.timestamp).toLocaleString('tr-TR')}
                                            </p>
                                        </div>

                                        {/* Telegram Butonu */}
                                        <Button
                                            onClick={() => handleSendProductToTelegram(diff)}
                                            disabled={isLoading}
                                            className="bg-blue-600 hover:bg-blue-700 text-white h-8 sm:h-10 px-3 sm:px-4 text-xs sm:text-sm flex-shrink-0"
                                        >
                                            <span className="text-sm sm:text-lg mr-1 sm:mr-2">📱</span>
                                            <span className="hidden sm:inline">Telegram</span>
                                        </Button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="text-center py-8 text-gray-500">
                            <Package className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                            <p>Henüz stok farkı bulunamadı</p>
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* Stok Takip Grafiği - Genişletilmiş */}
            <Card className="shadow-xl border-0 bg-white/80 backdrop-blur-sm relative z-20">
                <CardHeader className="pb-3 sm:pb-4">
                    <CardTitle className="flex items-center text-base sm:text-lg">
                        <TrendingUp className="w-4 h-4 sm:w-5 sm:h-5 mr-2 text-green-600" />
                        Stok Takip Grafiği
                    </CardTitle>
                </CardHeader>
                <CardContent className="px-4 sm:px-6 relative z-20">
                    <div className="space-y-3 sm:space-y-4">
                        <div className="relative z-30">
                            <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">
                                Ürün Seçin
                            </label>

                            {/* Arama Input */}
                            <div className="relative mb-3">
                                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                                <input
                                    type="text"
                                    placeholder="Ürün ara... (isim, SKU, kategori, marka)"
                                    value={productSearch}
                                    onChange={(e) => setProductSearch(e.target.value)}
                                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-xs sm:text-sm bg-white shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                />
                            </div>

                            {/* Ürün Listesi */}
                            <div className="max-h-60 overflow-y-auto border border-gray-200 rounded-lg bg-white">
                                {productsError ? (
                                    <div className="p-4 text-center text-red-500 text-sm">
                                        <p className="font-medium">Hata: {productsError.message}</p>
                                        <p className="text-xs mt-1">Ürünler yüklenirken bir hata oluştu</p>
                                    </div>
                                ) : allProducts && allProducts.length > 0 ? (
                                    filteredProducts.length > 0 ? (
                                        filteredProducts.map((product) => (
                                            <div
                                                key={product.id}
                                                onClick={() => setSelectedProduct(product.id)}
                                                className={`p-3 cursor-pointer hover:bg-blue-50 border-b border-gray-100 last:border-b-0 ${selectedProduct === product.id ? 'bg-blue-100' : ''
                                                    }`}
                                            >
                                                <div className="flex justify-between items-start">
                                                    <div className="flex-1 min-w-0">
                                                        <p className="font-medium text-sm text-gray-800 truncate">
                                                            {product.name}
                                                        </p>
                                                        <p className="text-xs text-gray-500">SKU: {product.sku}</p>
                                                        <div className="flex items-center space-x-2 mt-1">
                                                            <span className="text-xs text-gray-600">Stok: {product.stock}</span>
                                                            <span className="text-xs text-gray-600">₺{product.price}</span>
                                                            {product.category && (
                                                                <span className="text-xs text-blue-600 bg-blue-100 px-2 py-1 rounded">
                                                                    {product.category}
                                                                </span>
                                                            )}
                                                            {product.brand && (
                                                                <span className="text-xs text-green-600 bg-green-100 px-2 py-1 rounded">
                                                                    {product.brand}
                                                                </span>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        ))
                                    ) : (
                                        <div className="p-4 text-center text-gray-500 text-sm">
                                            {productSearch ? 'Arama kriterlerine uygun ürün bulunamadı' : 'Ürün yükleniyor...'}
                                        </div>
                                    )
                                ) : isLoadingProducts ? (
                                    <div className="p-4 text-center text-blue-500 text-sm">
                                        <div className="flex items-center justify-center gap-2">
                                            <RefreshCw className="w-4 h-4 animate-spin" />
                                            <p className="font-medium">Ürünler yükleniyor...</p>
                                        </div>
                                        <p className="text-xs mt-1 text-gray-500">Bu işlem birkaç dakika sürebilir</p>
                                    </div>
                                ) : (
                                    <div className="p-4 text-center text-gray-500 text-sm">
                                        <p className="font-medium">Ürünler yüklenmemiş</p>
                                        <p className="text-xs mt-1">Güncel grafikler için "Verileri Yenile" butonuna tıklayın</p>
                                    </div>
                                )}
                            </div>
                        </div>

                        {selectedProduct && chartData && chartData.length > 0 ? (
                            <div className="space-y-4">
                                <h4 className="font-medium text-sm text-gray-700">
                                    Stok Değişim Geçmişi
                                </h4>

                                {/* Grafik */}
                                <div className="h-48 sm:h-64">
                                    <Line
                                        data={{
                                            labels: chartData.map(data =>
                                                new Date(data.timestamp).toLocaleDateString('tr-TR', {
                                                    day: '2-digit',
                                                    month: '2-digit',
                                                    year: 'numeric',
                                                    hour: '2-digit',
                                                    minute: '2-digit'
                                                })
                                            ),
                                            datasets: [
                                                {
                                                    label: 'Stok Miktarı',
                                                    data: chartData.map(data => data.stock),
                                                    borderColor: 'rgb(59, 130, 246)',
                                                    backgroundColor: 'rgba(59, 130, 246, 0.1)',
                                                    tension: 0.1,
                                                    fill: false,
                                                }
                                            ]
                                        }}
                                        options={{
                                            responsive: true,
                                            maintainAspectRatio: false,
                                            plugins: {
                                                title: {
                                                    display: true,
                                                    text: 'Stok Değişim Grafiği'
                                                },
                                                legend: {
                                                    position: 'top' as const,
                                                }
                                            },
                                            scales: {
                                                y: {
                                                    beginAtZero: true,
                                                    title: {
                                                        display: true,
                                                        text: 'Stok Miktarı'
                                                    }
                                                },
                                                x: {
                                                    title: {
                                                        display: true,
                                                        text: 'Tarih'
                                                    }
                                                }
                                            }
                                        }}
                                    />
                                </div>

                                {/* Son Değerler */}
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                                    <div className="p-2 sm:p-3 bg-blue-50 rounded-lg">
                                        <div className="flex items-center space-x-2">
                                            <Package className="w-3 h-3 sm:w-4 sm:h-4 text-blue-600" />
                                            <span className="text-xs sm:text-sm font-medium text-blue-800">İlk Stok</span>
                                        </div>
                                        <p className="text-sm sm:text-lg font-bold text-blue-600 mt-1">
                                            {chartData[0]?.stock || 0}
                                        </p>
                                    </div>
                                    <div className="p-2 sm:p-3 bg-green-50 rounded-lg">
                                        <div className="flex items-center space-x-2">
                                            <Target className="w-3 h-3 sm:w-4 sm:h-4 text-green-600" />
                                            <span className="text-xs sm:text-sm font-medium text-green-800">Son Stok</span>
                                        </div>
                                        <p className="text-sm sm:text-lg font-bold text-green-600 mt-1">
                                            {chartData[chartData.length - 1]?.stock || 0}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        ) : selectedProduct ? (
                            <div className="text-center py-8 text-gray-500">
                                <BarChart3 className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                                <p>Bu ürün için stok geçmişi bulunamadı</p>
                            </div>
                        ) : (
                            <div className="text-center py-8 text-gray-500">
                                <Target className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                                <p>Grafik görüntülemek için ürün seçin</p>
                            </div>
                        )}
                    </div>
                </CardContent>
            </Card>

            {/* Son 1 Haftada Stoğu Sıfıra Düşen Ürünler */}
            <Card className="mt-6 sm:mt-8 shadow-xl border-0 bg-white/80 backdrop-blur-sm">
                <CardHeader className="pb-3 sm:pb-4">
                    <CardTitle className="flex items-center text-lg sm:text-xl">
                        <TrendingDown className="w-5 h-5 sm:w-6 sm:h-6 mr-2 sm:mr-3 text-red-600" />
                        Son 1 Haftada Stoğu Sıfıra Düşen Ürünler
                    </CardTitle>
                </CardHeader>
                <CardContent className="px-4 sm:px-6">
                    {isLoadingDepleted ? (
                        <div className="text-center py-6 sm:py-8">
                            <RefreshCw className="w-5 h-5 sm:w-6 sm:h-6 animate-spin mx-auto mb-2" />
                            <p className="text-sm sm:text-base">Son 1 haftada stoğu sıfıra düşen ürünler yükleniyor...</p>
                        </div>
                    ) : depletedError ? (
                        <div className="text-center py-6 sm:py-8 text-red-600">
                            <p className="text-sm sm:text-base">Hata: {depletedError.message}</p>
                        </div>
                    ) : weeklyDepletedProducts && weeklyDepletedProducts.length > 0 ? (
                        <div className="space-y-3 sm:space-y-4 max-h-96 overflow-y-auto">
                            {weeklyDepletedProducts.map((product) => (
                                <div key={product.id} className="flex flex-col sm:flex-row items-start sm:items-center justify-between p-3 sm:p-4 bg-gradient-to-r from-red-50 to-orange-50 rounded-lg border border-red-200 gap-3 sm:gap-4">
                                    <div className="flex items-center space-x-3 sm:space-x-4 w-full sm:w-auto">
                                        {/* Ürün Görseli - Mobil Uyumlu */}
                                        <div
                                            className="w-16 h-16 sm:w-20 sm:h-20 lg:w-24 lg:h-24 bg-gray-100 rounded-lg flex items-center justify-center overflow-hidden cursor-pointer hover:shadow-lg transition-shadow flex-shrink-0"
                                            onClick={() => product.image1 && handleImageClick(product.image1)}
                                        >
                                            {product.image1 ? (
                                                <img
                                                    src={product.image1}
                                                    alt={product.name}
                                                    className="w-full h-full object-cover hover:scale-105 transition-transform"
                                                />
                                            ) : (
                                                <Package className="w-6 h-6 sm:w-8 sm:h-8 lg:w-12 lg:h-12 text-gray-400" />
                                            )}
                                        </div>

                                        {/* Ürün Bilgileri */}
                                        <div className="flex-1 min-w-0">
                                            <p className="font-medium text-gray-800 text-sm sm:text-base truncate">{product.name}</p>
                                            <p className="text-xs sm:text-sm text-gray-600">SKU: {product.sku}</p>
                                            <div className="flex flex-col sm:flex-row sm:items-center space-y-1 sm:space-y-0 sm:space-x-4 mt-1">
                                                <span className="text-xs sm:text-sm text-red-600 font-medium">
                                                    Stok: {product.stock} adet
                                                </span>
                                                <span className="text-xs sm:text-sm text-gray-600">
                                                    ₺{product.price}
                                                </span>
                                            </div>
                                            <p className="text-xs text-gray-500 mt-1">
                                                Son güncelleme: {new Date(product.lastUpdated).toLocaleString('tr-TR')}
                                            </p>
                                            {product.depletedAt && (
                                                <p className="text-xs text-red-600 font-medium mt-1">
                                                    Stoğu sıfıra düştü: {new Date(product.depletedAt).toLocaleString('tr-TR')}
                                                </p>
                                            )}
                                        </div>
                                    </div>

                                    {/* Sağ Taraf - Durum ve Butonlar */}
                                    <div className="flex items-center justify-between sm:justify-end space-x-3 sm:space-x-4 w-full sm:w-auto">
                                        {/* Durum Göstergesi */}
                                        <div className="text-right">
                                            <div className="flex items-center space-x-2 text-red-600">
                                                <TrendingDown className="w-4 h-4 sm:w-5 sm:h-5" />
                                                <span className="text-sm sm:text-lg font-bold">Stok Sıfır</span>
                                            </div>
                                            <p className="text-xs text-gray-500 mt-1">
                                                {new Date(product.lastUpdated).toLocaleDateString('tr-TR')}
                                            </p>
                                        </div>

                                        {/* Telegram Butonu */}
                                        <Button
                                            onClick={() => {
                                                // Bu ürün için Telegram uyarısı gönder
                                                const mockStockDifference = {
                                                    id: 'mock-' + product.id,
                                                    productId: product.id,
                                                    sku: product.sku,
                                                    previousStock: 1, // Varsayılan değer
                                                    currentStock: 0,
                                                    difference: -1,
                                                    timestamp: new Date().toISOString(),
                                                    period: 'DAILY' as const,
                                                    product: product
                                                }
                                                handleSendProductToTelegram(mockStockDifference)
                                            }}
                                            disabled={isLoading}
                                            className="bg-blue-600 hover:bg-blue-700 text-white h-8 sm:h-10 px-3 sm:px-4 text-xs sm:text-sm flex-shrink-0"
                                        >
                                            <span className="text-sm sm:text-lg mr-1 sm:mr-2">📱</span>
                                            <span className="hidden sm:inline">Telegram</span>
                                            <span className="sm:hidden">Telegram</span>
                                        </Button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="text-center py-6 sm:py-8 text-gray-500">
                            <Package className="w-10 h-10 sm:w-12 sm:h-12 mx-auto mb-4 text-gray-300" />
                            <p className="text-sm sm:text-base">Son 1 haftada stoğu sıfıra düşen ürün bulunamadı</p>
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* Görsel Modal */}
            {showImageModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={() => setShowImageModal(false)}>
                    <div className="bg-white rounded-lg p-4 max-w-4xl max-h-4xl">
                        <div className="flex justify-between items-center mb-4">
                            <h3 className="text-lg font-semibold">Ürün Görseli</h3>
                            <button
                                onClick={() => setShowImageModal(false)}
                                className="text-gray-500 hover:text-gray-700 text-2xl"
                            >
                                ×
                            </button>
                        </div>
                        <img
                            src={selectedImage}
                            alt="Ürün Görseli"
                            className="max-w-full max-h-96 object-contain rounded-lg"
                        />
                    </div>
                </div>
            )}
        </div>
    )
}
